import React, { useState } from 'react'
import { observer } from 'mobx-react-lite'
import {
    Card,
    CardBody,
    CardHeader,
    Col,
    Row,
    Button,
    FormInput,
    useUI
} from '@evertel/web/ui'

interface ReportingDepartmentGateProps {
    roomName?: string
    roomDescription?: string
    currentStep?: number
    totalSteps?: number
    onSave?: (departmentName: string, position: string) => Promise<void>
}

const ReportingDepartmentGate = observer(({
    roomName = 'Room Name',
    roomDescription = 'Room Description',
    currentStep,
    totalSteps,
    onSave
}: ReportingDepartmentGateProps) => {

    const { addToast } = useUI()

    const [department, setDepartment] = useState('')
    const [position, setPosition] = useState('')
    const [isSaving, setIsSaving] = useState(false)

    const isSaveDisabled = isSaving || !department.trim() || !position.trim()

    const handleSave = async () => {
        const trimmedDepartment = department.trim()
        const trimmedPosition = position.trim()
        if (!trimmedDepartment || !trimmedPosition) return

        try {
            setIsSaving(true)
            await onSave?.(trimmedDepartment, trimmedPosition)
        } catch (e: any) {
            addToast({
                color: 'danger',
                message: e?.message || 'Failed to save reporting agency.'
            })
        } finally {
            setIsSaving(false)
        }
    }

    const displayRoomName = roomName?.trim() || 'No Room Name'

    return (
        <div className="animated fadeIn">
            <Card>
                <CardHeader className="py-3">
                    <h3 className="mb-0">
                        <span className="text-muted me-1">#</span>
                        <strong>{displayRoomName}</strong>
                        <small className="ms-3 fw-normal" style={{ fontSize: '0.9rem' }}>
                            {roomDescription}
                        </small>
                    </h3>
                </CardHeader>
                <CardBody>
                    <h5 className="fw-bold mb-2">Enter Your Reporting Agency</h5>
                    <p className="text-muted mb-4">
                        To access this room, please identify your reporting agency and position.
                    </p>

                    <Row>
                        <Col xs={6} className="pe-2">
                            <FormInput
                                name="department"
                                label="Reporting Agency"
                                placeholder="Enter agency name"
                                value={department}
                                onChange={(e) => setDepartment(e.target.value)}
                                maxLength={64}
                            />
                        </Col>
                        <Col xs={6}>
                            <FormInput
                                name="position"
                                label="Position"
                                placeholder="e.g., Detective, Officer"
                                value={position}
                                onChange={(e) => setPosition(e.target.value)}
                                maxLength={64}
                            />
                        </Col>
                    </Row>

                    <div className="d-flex justify-content-between align-items-center mt-3">
                        {currentStep !== undefined && totalSteps !== undefined && totalSteps > 1 ? (
                            <small className="text-muted">Step {currentStep} of {totalSteps}</small>
                        ) : (
                            <span />
                        )}
                        <Button
                            color="primary"
                            onClick={handleSave}
                            disabled={isSaveDisabled}
                            loading={isSaving}
                        >
                            Save
                        </Button>
                    </div>
                </CardBody>
            </Card>
        </div>
    )
})

export { ReportingDepartmentGate }
